'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Zap, Clock, Target, Lightbulb, Star, Eye, Brain, Heart, ArrowRight, Circle, Sparkles } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function ChakraYayilimiPage() {
  const chakras = [
    {
      position: 1,
      name: "Kök Chakra",
      sanskrit: "Muladhara",
      color: "Kırmızı",
      location: "Kuyruk soğuğu",
      element: "Toprak",
      theme: "Hayatta kalma, güvenlik, temeller",
      blockages: "Korku, anksiyete, maddi sıkıntı",
      balance: "Güvenlik hissi, topraklanma, denge"
    },
    {
      position: 2,
      name: "Sakral Chakra",
      sanskrit: "Svadhisthana",
      color: "Turuncu",
      location: "Kasmağın altı",
      element: "Su",
      theme: "Cinsellik, yaratıcılık, duygu",
      blockages: "Cinsel sorunlar, yaratıcı blokaj",
      balance: "Tutkulu yaşam, yaratıcılık akışı"
    },
    {
      position: 3,
      name: "Solar Plexus",
      sanskrit: "Manipura",
      color: "Sarı",
      location: "Mide bölgesi",
      element: "Ateş",
      theme: "Kişisel güç, özgüven, kontrol",
      blockages: "Özgüven eksikliği, kontrol takntısı",
      balance: "Güçlü özgüven, kişisel güç"
    },
    {
      position: 4,
      name: "Kalp Chakrası",
      sanskrit: "Anahata",
      color: "Yeşil",
      location: "Göğüs merkezi",
      element: "Hava",
      theme: "Sevgi, merhamet, bağlantı",
      blockages: "Kalp kırıklığı, kapanmışlık",
      balance: "Koşulsuz sevgi, empati, bağışlama"
    },
    {
      position: 5,
      name: "Boğaz Chakrası",
      sanskrit: "Vishuddha",
      color: "Mavi",
      location: "Boğaz",
      element: "Eter",
      theme: "İletişim, hakikat, ifade",
      blockages: "Konuşma zorluğu, yalan söyleme",
      balance: "Net iletişim, hakikati söyleme"
    },
    {
      position: 6,
      name: "Üçüncü Göz",
      sanskrit: "Ajna",
      color: "Mor",
      location: "Kaşlar arası",
      element: "Işık",
      theme: "Sezgi, bilgi, görü",
      blockages: "Sezgileri görmezden gelme",
      balance: "Güçlü sezgi, iç bilgi"
    },
    {
      position: 7,
      name: "Taç Chakrası",
      sanskrit: "Sahasrara",
      color: "Beyaz/Mor",
      location: "Başın tepesi",
      element: "Düşünce",
      theme: "Ruhsal bağlantı, aşkınlık",
      blockages: "Ruhsal bağlantısızlık",
      balance: "Evrenle birlik, aşkın bilinç"
    }
  ];

  const healingMethods = [
    {
      chakra: "Kök",
      methods: ["Doğada zaman geçirme", "Kırmızı yiyecekler", "Topraklama egzersizleri"],
      cards: ["Dünya", "Tılsım Kartları", "Kök-temelleri vurgulayan kartlar"]
    },
    {
      chakra: "Sakral",
      methods: ["Dans etme", "Yaratıcı hobiler", "Su terapileri"],
      cards: ["Kupa Kartları", "Ay", "Yaratıcılığı vurgulayan kartlar"]
    },
    {
      chakra: "Solar Plexus",
      methods: ["Güneş ışığı", "Sarı yiyecekler", "Özgüven egzersizleri"],
      cards: ["Güneş", "Değnek Ası", "Güç kartları"]
    },
    {
      chakra: "Kalp",
      methods: ["Sevgi meditasyonu", "Yeşil doğa", "Bağışlama çalışması"],
      cards: ["Aşıklar", "Kupa İkisi", "Sevgiyi vurgulayan kartlar"]
    },
    {
      chakra: "Boğaz",
      methods: ["Şarkı söyleme", "Mavi renkler", "Doğru iletişim"],
      cards: ["Kılıç Kartları", "Adalet", "İletişim kartları"]
    },
    {
      chakra: "Üçüncü Göz",
      methods: ["Meditasyon", "Sezgi egzersizleri", "Mor işık"],
      cards: ["Yüksek Rahibe", "Ay", "Sezgisel kartlar"]
    },
    {
      chakra: "Taç",
      methods: ["Ruhsal pratikler", "Meditasyon", "Beyaz ışık"],
      cards: ["Yıldız", "Deli", "Ruhsal kartlar"]
    }
  ];

  const interpretationTips = [
    {
      tip: "Renk Uyumu",
      detail: "Kartın renkleri chakranın rengiyle uyumlu mu? Bu harmonik enerjiyi gösterir."
    },
    {
      tip: "Element Bağlantısı",
      detail: "Kartların elementi chakranın elementiyle eşleşiyor mu? (Toprak-Kök, Su-Sakral, vb.)"
    },
    {
      tip: "Blokaj İşaretleri",
      detail: "Ters kartlar, olumsuz figürler veya siyah renkler blokajları işaret edebilir."
    },
    {
      tip: "Denge Durumu",
      detail: "Tüm chakralardaki kartları karşılaştırın. Hangi chakralar güçlü, hangiler zayıf?"
    }
  ];

  const healingQuestions = [
    "Hangi chakram en çok desteğe ihtiyacı duyuyor?",
    "Enerji dengemi nasıl restore edebilirim?",
    "Hangi alanda ruhsal gelişimime odaklanmalıyım?",
    "Fiziksel belirtilerimin ruhsal kökeni nedir?",
    "Duygusal blokajlarımı nasıl açabilirim?",
    "Ruhsal bağlantımı nasıl güçlendirebilirim?"
  ];

  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Okuma Teknikleri</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Chakra Yayılımı</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/interpretation-techniques"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Okuma Teknikleri
          </Link>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-purple-900/20 to-indigo-900/20 rounded-xl p-8 mb-8 border border-purple-500/20">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-700 rounded-xl flex items-center justify-center">
              <Zap className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-100">Chakra Yayılımı</h1>
              <p className="text-purple-300 font-medium">İleri Seviye • 7 Kart</p>
            </div>
          </div>
          <p className="text-lg text-slate-200 leading-relaxed">
            7 ana chakranın enerji durumunu değerlendirir. Ruhsal sağlık, enerji dengesi ve 
            kişisel gelişim için kullanılır. Her chakranın durumunu analiz ederek bütünsel iyileşme sunar.
          </p>
        </div>

        {/* Ana Bilgiler */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Clock className="h-6 w-6 text-purple-400" />
              <h3 className="text-lg font-semibold text-slate-100">Süre</h3>
            </div>
            <p className="text-slate-300">30-45 dakika</p>
            <p className="text-slate-400 text-sm">Ruhsal odaklı analiz</p>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Target className="h-6 w-6 text-purple-400" />
              <h3 className="text-lg font-semibold text-slate-100">Amaç</h3>
            </div>
            <p className="text-slate-300">Enerji dengesi</p>
            <p className="text-slate-400 text-sm">Ruhsal sağlık</p>
          </div>

          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div className="flex items-center gap-3 mb-3">
              <Lightbulb className="h-6 w-6 text-purple-400" />
              <h3 className="text-lg font-semibold text-slate-100">Seviye</h3>
            </div>
            <p className="text-slate-300">İleri</p>
            <p className="text-slate-400 text-sm">Chakra bilgisi gerekli</p>
          </div>
        </div>

        {/* Chakra Dizilimi */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Chakra Dizilimi</h2>
          <div className="bg-slate-800 rounded-xl p-8 border border-slate-700">
            <div className="flex flex-col items-center gap-3 max-w-xs mx-auto">
              {/* Yukarıdan aşağıya doğru chakralar */}
              <div className="w-20 h-20 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                7<br/><span className="text-xs">Taç</span>
              </div>
              <div className="w-20 h-20 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                6<br/><span className="text-xs">Üçüncü Göz</span>
              </div>
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                5<br/><span className="text-xs">Boğaz</span>
              </div>
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center text-white font-bold">
                4<br/><span className="text-xs">Kalp</span>
              </div>
              <div className="w-20 h-20 bg-yellow-600 rounded-full flex items-center justify-center text-white font-bold">
                3<br/><span className="text-xs">Solar</span>
              </div>
              <div className="w-20 h-20 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold">
                2<br/><span className="text-xs">Sakral</span>
              </div>
              <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center text-white font-bold">
                1<br/><span className="text-xs">Kök</span>
              </div>
            </div>
            <p className="text-slate-400 text-center mt-4 text-sm">
              Kartları yukarıdan aşağıya doğru (7'den 1'e) yerleştirin
            </p>
          </div>
        </div>

        {/* 7 Chakra Detayları */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">7 Ana Chakra</h2>
          <div className="space-y-4">
            {chakras.map((chakra) => (
              <div key={chakra.position} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                      chakra.position === 1 ? 'bg-red-600' :
                      chakra.position === 2 ? 'bg-orange-600' :
                      chakra.position === 3 ? 'bg-yellow-600' :
                      chakra.position === 4 ? 'bg-green-600' :
                      chakra.position === 5 ? 'bg-blue-600' :
                      chakra.position === 6 ? 'bg-indigo-600' :
                      'bg-purple-600'
                    }`}>
                      {chakra.position}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-100">{chakra.name}</h3>
                      <p className="text-slate-400 text-sm italic">{chakra.sanskrit}</p>
                      <p className="text-slate-300 text-xs">{chakra.location}</p>
                    </div>
                  </div>
                  
                  <div>
                    <div className="space-y-2">
                      <div>
                        <span className="text-slate-400 text-xs">Element:</span>
                        <span className="text-slate-300 text-sm ml-2">{chakra.element}</span>
                      </div>
                      <div>
                        <span className="text-slate-400 text-xs">Tema:</span>
                        <span className="text-slate-300 text-sm ml-2">{chakra.theme}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="space-y-2">
                      <div>
                        <span className="text-red-400 text-xs">Blokaj:</span>
                        <span className="text-slate-300 text-sm ml-2">{chakra.blockages}</span>
                      </div>
                      <div>
                        <span className="text-green-400 text-xs">Denge:</span>
                        <span className="text-slate-300 text-sm ml-2">{chakra.balance}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* İyileştirme Yöntemleri */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">İyileştirme Rehberi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {healingMethods.map((method, index) => (
              <div key={index} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                  <Circle className={`h-5 w-5 ${
                    method.chakra === 'Kök' ? 'text-red-500' :
                    method.chakra === 'Sakral' ? 'text-orange-500' :
                    method.chakra === 'Solar Plexus' ? 'text-yellow-500' :
                    method.chakra === 'Kalp' ? 'text-green-500' :
                    method.chakra === 'Boğaz' ? 'text-blue-500' :
                    method.chakra === 'Üçüncü Göz' ? 'text-indigo-500' :
                    'text-purple-500'
                  }`} />
                  {method.chakra} Chakra
                </h3>
                
                <div className="mb-4">
                  <h4 className="text-slate-200 text-sm font-medium mb-2">Pratik Yöntemler:</h4>
                  <div className="space-y-1">
                    {method.methods.map((m, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-purple-400 rounded-full"></div>
                        <span className="text-slate-300 text-sm">{m}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="text-slate-200 text-sm font-medium mb-2">Destekleyici Kartlar:</h4>
                  <div className="space-y-1">
                    {method.cards.map((card, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full"></div>
                        <span className="text-slate-300 text-sm">{card}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Yorumlama İpuçları */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yorumlama İpuçları</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {interpretationTips.map((tip, index) => (
              <div key={index} className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                  <Brain className="h-5 w-5 text-purple-400" />
                  {tip.tip}
                </h3>
                <p className="text-slate-300">{tip.detail}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Soru Örnekleri */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Uygun Sorular</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {healingQuestions.map((question, index) => (
              <div key={index} className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                <div className="flex items-start gap-3">
                  <Heart className="h-4 w-4 text-purple-400 mt-1 flex-shrink-0" />
                  <p className="text-slate-300 text-sm">{question}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Okuma Süreci */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Adım Adım Süreç</h2>
          <div className="space-y-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Hazırlık ve Merkez Alma</h3>
                  <p className="text-slate-300 mb-2">
                    Sakin ve huzurlu bir ortamda, derin nefes alın. Chakralarınızı hissetmeye çalışın.
                    Enerji akanını bilinçli olarak hissedin.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Kartları Yerleştirin</h3>
                  <p className="text-slate-300 mb-2">
                    7. chakradan (Taç) başlayarak 1. chakraya (Kök) kadar kartları sırayla yerleştirin.
                    Her kartı yerleştirirken o chakranın konumunu düşünün.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Bireysel Chakra Analizi</h3>
                  <p className="text-slate-300 mb-2">
                    Her chakrayı ayrı ayrı analiz edin. Kart o chakranın rengine uygun mu?
                    Blokaj mı yoksa denge mi gösteriyor? Hangi iyileştirme yöntemine ihtiyaç var?
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white text-sm font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-100 mb-2">Bütünsel Değerlendirme</h3>
                  <p className="text-slate-300">
                    Tüm chakraları birlikte değerlendirin. Hangileri güçlü, hangileri zayıf?
                    Enerji akışı engelleyen noktalar var mı? Dengeli bir iyileşme planı oluşturun.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Önemli Uyarılar */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Önemli Notlar</h2>
          <div className="space-y-4">
            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-200 mb-3">🧘 Fiziksel Sağlık Yerine Geçmez</h3>
              <p className="text-amber-100">
                Chakra okumaları tıbbi tedavinin yerine geçmez. Ciddi sağlık sorunları için
                mutlaka tıbbi yardım alın.
              </p>
            </div>
            
            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-200 mb-3">⚙️ Dengeyi Koruyun</h3>
              <p className="text-amber-100">
                Tek bir chakrayı açır şekilde uyarmaktan kaçının. Tüm sistem dengeli çalışmalıdır.
              </p>
            </div>

            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-amber-200 mb-3">🕰️ Zamanla Gelişir</h3>
              <p className="text-amber-100">
                Chakra iyileştirmesi zaman alır. Sabırlı olun ve düzenli pratik yapın.
              </p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/interpretation-techniques/zaman-cizelgesi"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: Zaman Çizelgesi
          </Link>

          <Link
            href="/about/tarot/interpretation-techniques"
            className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            Tüm Tekniklere Dön
            <Sparkles className="h-4 w-4 ml-2" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}